import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  CheckCircle2,
  XCircle,
  UserCheck,
  Briefcase,
  TrendingUp,
} from 'lucide-react';
import type { RoleEligibility } from '@/data/eligibility';
import type { EmploymentStatus } from '@/data/types';

interface EligibilitySectionProps {
  eligibility: RoleEligibility;
  variant?: 'physician' | 'non-physician';
  employmentStatus?: EmploymentStatus;
  className?: string;
}

export function EligibilitySection({
  eligibility,
  variant = 'physician',
  employmentStatus,
  className,
}: EligibilitySectionProps) {
  const accentBg = variant === 'physician' ? 'bg-eventus-purple' : 'bg-eventus-teal';
  const accentBgLight =
    variant === 'physician' ? 'bg-eventus-purple/10' : 'bg-eventus-teal/10';
  const accentText =
    variant === 'physician' ? 'text-eventus-purple' : 'text-eventus-teal-dark';

  // Map criteria to icons
  const criteriaIcons = [UserCheck, Briefcase, TrendingUp];

  return (
    <Card className={cn('border-2 border-gray-200 overflow-hidden', className)}>
      {/* Top row: Criteria + Award side by side */}
      <div className="grid md:grid-cols-[1fr_auto] divide-y md:divide-y-0 md:divide-x divide-gray-200">
        {/* Criteria */}
        <div className="p-6">
          <div className={cn('inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold text-white mb-4', accentBg)}>
            <CheckCircle2 className="w-4 h-4" />
            Who Qualifies
          </div>
          <div className="space-y-3">
            {eligibility.criteria.map((criterion, index) => {
              const Icon = criteriaIcons[index] || CheckCircle2;
              return (
                <div key={criterion.label} className="flex items-center gap-3">
                  <div
                    className={cn(
                      'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                      accentBgLight
                    )}
                  >
                    <Icon className={cn('w-4 h-4', accentText)} />
                  </div>
                  <div>
                    <span className="font-semibold text-eventus-navy text-sm">
                      {criterion.label}
                    </span>
                    <span className="text-gray-500 text-sm ml-1.5">
                      {criterion.description}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Award — compact right column */}
        <div className="p-6 flex flex-col items-center justify-center md:min-w-[220px]">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
            Max Quarterly Award
          </div>
          {employmentStatus !== 'part-time' && (
            <div className="text-center">
              <div className="text-4xl font-bold text-eventus-navy">
                ${eligibility.maxAward.fullTime.toLocaleString()}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                <Badge variant="secondary">Full-Time</Badge>
                <span className="ml-1.5">per quarter</span>
              </div>
            </div>
          )}
          {employmentStatus !== 'full-time' && (
            <div className={cn('text-center', employmentStatus === undefined && 'mt-3 pt-3 border-t border-gray-200')}>
              <div className="text-4xl font-bold text-eventus-navy">
                {eligibility.maxAward.perHour
                  ? <>${eligibility.maxAward.perHour}<span className="text-lg font-normal text-gray-400">/hr</span></>
                  : <>${eligibility.maxAward.perDay}<span className="text-lg font-normal text-gray-400">/day</span></>
                }
              </div>
              <div className="text-sm text-gray-500 mt-1">
                <Badge variant="outline">Part-Time</Badge>
                <span className="ml-1.5">{eligibility.maxAward.perHour ? '× avg hours' : '× avg days'}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Part-time example — hidden for full-time employees */}
      {employmentStatus !== 'full-time' && (
        <div className="px-6 pb-4">
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-sm text-blue-800">
              {eligibility.maxAward.perHour ? (
                <>
                  <strong>Example:</strong> Working 30 hrs/week → max{' '}
                  <strong>${Math.round(eligibility.maxAward.perHour * 30).toLocaleString()}</strong>/quarter
                </>
              ) : (
                <>
                  <strong>Example:</strong> Working 3 days/week → max{' '}
                  <strong>${(eligibility.maxAward.perDay * 3).toLocaleString()}</strong>/quarter
                </>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Exclusions — inline footer */}
      {eligibility.exclusions.length > 0 && (
        <div className="border-t border-gray-200 bg-red-50/40 px-6 py-4">
          <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
            <span className="flex items-center gap-1.5 text-xs font-semibold text-red-700 uppercase tracking-wider">
              <XCircle className="w-3.5 h-3.5" />
              Not Eligible
            </span>
            {eligibility.exclusions.map((exclusion) => (
              <span key={exclusion.label} className="flex items-center gap-1.5 text-sm text-red-800">
                <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                <span><strong>{exclusion.label}</strong> — {exclusion.description}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
